import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { FiAlertCircle, FiAlertTriangle, FiCheckCircle, FiInfo } from "react-icons/fi";
import { fontFamily } from "../components/shared/styles/theme";
import { primaryColor, white } from "../components/shared/styles/color";

const AppDialogContext = createContext(null);

const VARIANTS = {
  info: { Icon: FiInfo, color: primaryColor, bg: "#EFF6FF" },
  success: { Icon: FiCheckCircle, color: "#15803D", bg: "#ECFDF5" },
  warning: { Icon: FiAlertTriangle, color: "#B45309", bg: "#FFFBEB" },
  error: { Icon: FiAlertCircle, color: "#DC2626", bg: "#FEF2F2" },
};

function normalizeMessage(input) {
  if (input == null) return "";
  if (typeof input === "string") return input;
  if (input instanceof Error) return input.message || "Something went wrong.";
  try {
    return String(input);
  } catch {
    return "Something went wrong.";
  }
}

function AppDialogHost({ dialog, onClose }) {
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (!dialog) return undefined;
    setInputValue(dialog.inputValue || "");
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [dialog, onClose]);

  if (!dialog) return null;

  const variant = VARIANTS[dialog.variant] || VARIANTS.info;
  const Icon = variant.Icon;
  const isConfirm = dialog.type === "confirm";
  const isPrompt = dialog.type === "prompt";

  const submitPrompt = () => {
    if (dialog.inputValidator) {
      const err = dialog.inputValidator(inputValue);
      if (err) return;
    }
    onClose(true, inputValue);
  };

  return (
    <Overlay onClick={() => onClose(false)} role="presentation">
      <Dialog
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-dialog-title"
        aria-describedby="app-dialog-message"
        onClick={(e) => e.stopPropagation()}
      >
        <IconWrap $bg={variant.bg} $color={variant.color}>
          <Icon size={22} />
        </IconWrap>
        {dialog.title && <Title id="app-dialog-title">{dialog.title}</Title>}
        <Message id="app-dialog-message">{dialog.message}</Message>
        {isPrompt && (
          <PromptInput
            type={dialog.input === "number" ? "number" : "text"}
            value={inputValue}
            placeholder={dialog.inputPlaceholder || ""}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitPrompt();
            }}
            autoFocus
          />
        )}
        <Actions $single={!isConfirm && !isPrompt}>
          {(isConfirm || isPrompt) && (
            <SecondaryBtn type="button" onClick={() => onClose(false)}>
              {dialog.cancelText || "Cancel"}
            </SecondaryBtn>
          )}
          <PrimaryBtn
            type="button"
            $danger={dialog.variant === "error" && isConfirm}
            onClick={() => {
              if (isPrompt) submitPrompt();
              else onClose(true);
            }}
            autoFocus={!isPrompt}
          >
            {dialog.confirmText || (isConfirm || isPrompt ? "Confirm" : "OK")}
          </PrimaryBtn>
        </Actions>
      </Dialog>
    </Overlay>
  );
}

export function AppDialogProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const resolverRef = useRef(null);

  const closeDialog = useCallback((confirmed, promptValue) => {
    setDialog((current) => {
      if (current?.resolve) {
        if (current.type === "prompt") {
          current.resolve({
            isConfirmed: Boolean(confirmed),
            value: confirmed ? promptValue : undefined,
          });
        } else {
          current.resolve(Boolean(confirmed));
        }
      }
      return null;
    });
    resolverRef.current = null;
  }, []);

  const openDialog = useCallback((config) => {
    return new Promise((resolve) => {
      if (resolverRef.current) {
        resolverRef.current(false);
      }
      resolverRef.current = resolve;
      setDialog({ ...config, resolve });
    });
  }, []);

  const alert = useCallback(
    (options) => {
      const opts =
        typeof options === "string"
          ? { message: options }
          : options || {};
      return openDialog({
        type: "alert",
        title: opts.title || "Notice",
        message: normalizeMessage(opts.message),
        confirmText: opts.confirmText || "OK",
        variant: opts.variant || "info",
      });
    },
    [openDialog],
  );

  const confirm = useCallback(
    (options) => {
      const opts =
        typeof options === "string"
          ? { message: options }
          : options || {};
      return openDialog({
        type: "confirm",
        title: opts.title || "Please confirm",
        message: normalizeMessage(opts.message),
        confirmText: opts.confirmText || "Confirm",
        cancelText: opts.cancelText || "Cancel",
        variant: opts.variant || "warning",
      });
    },
    [openDialog],
  );

  const prompt = useCallback(
    (options) => {
      const opts =
        typeof options === "string"
          ? { message: options }
          : options || {};
      return openDialog({
        type: "prompt",
        title: opts.title || "Enter a value",
        message: normalizeMessage(opts.message),
        confirmText: opts.confirmText || "OK",
        cancelText: opts.cancelText || "Cancel",
        variant: opts.variant || "info",
        input: opts.input || "text",
        inputPlaceholder: opts.inputPlaceholder || "",
        inputValue: opts.inputValue || "",
        inputValidator: opts.inputValidator || null,
      });
    },
    [openDialog],
  );

  useEffect(() => {
    registerAppDialogBridge({ alert, confirm, prompt, dismiss: closeDialog });
    return () => registerAppDialogBridge(null);
  }, [alert, confirm, prompt, closeDialog]);

  const value = useMemo(() => ({ alert, confirm, prompt }), [alert, confirm, prompt]);

  return (
    <AppDialogContext.Provider value={value}>
      {children}
      <AppDialogHost dialog={dialog} onClose={closeDialog} />
    </AppDialogContext.Provider>
  );
}

export function useAppDialog() {
  const ctx = useContext(AppDialogContext);
  if (!ctx) {
    throw new Error("useAppDialog must be used within AppDialogProvider");
  }
  return ctx;
}

/** Imperative helpers for modules that cannot use the hook directly. */
let bridge = null;

export function registerAppDialogBridge(next) {
  bridge = next;
}

export function appAlert(options) {
  if (bridge?.alert) return bridge.alert(options);
  const message = normalizeMessage(
    typeof options === "string" ? options : options?.message,
  );
  window.alert(message);
  return Promise.resolve(true);
}

export function appConfirm(options) {
  if (bridge?.confirm) return bridge.confirm(options);
  const message = normalizeMessage(
    typeof options === "string" ? options : options?.message,
  );
  return Promise.resolve(window.confirm(message));
}

export function appPrompt(options) {
  if (bridge?.prompt) return bridge.prompt(options);
  const message = normalizeMessage(
    typeof options === "string" ? options : options?.message,
  );
  const value = window.prompt(message);
  if (value == null) return Promise.resolve({ isConfirmed: false, value: undefined });
  return Promise.resolve({ isConfirmed: true, value });
}

export function appDismissDialog() {
  if (bridge?.dismiss) {
    bridge.dismiss(false);
    return;
  }
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  /* Above review drawers (100000) and chat widget (~999998) so confirms are never trapped behind them */
  z-index: 1100000;
  box-sizing: border-box;
`;

const Dialog = styled.div`
  width: min(420px, 100%);
  background: ${white};
  border-radius: 14px;
  padding: 24px 24px 20px;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.18);
  text-align: center;
  animation: dialogIn 0.18s ease-out;

  @keyframes dialogIn {
    from {
      opacity: 0;
      transform: scale(0.96) translateY(8px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const IconWrap = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 14px;
`;

const Title = styled.h2`
  font-family: ${fontFamily.font};
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 8px;
  line-height: 1.35;
`;

const Message = styled.p`
  font-family: ${fontFamily.font};
  font-size: 14px;
  line-height: 1.55;
  color: #4b5563;
  margin: 0 0 20px;
  white-space: pre-wrap;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: ${(p) => (p.$single ? "center" : "stretch")};

  button {
    flex: ${(p) => (p.$single ? "0 1 auto" : "1")};
    min-width: ${(p) => (p.$single ? "120px" : "0")};
  }
`;

const PrimaryBtn = styled.button`
  border: 0;
  background: ${(p) => (p.$danger ? "#DC2626" : primaryColor)};
  color: ${white};
  font-family: ${fontFamily.font};
  font-size: 14px;
  font-weight: 600;
  padding: 11px 18px;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    opacity: 0.92;
  }
`;

const SecondaryBtn = styled.button`
  border: 1px solid #d1d5db;
  background: ${white};
  color: #374151;
  font-family: ${fontFamily.font};
  font-size: 14px;
  font-weight: 600;
  padding: 11px 18px;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
  }
`;

const PromptInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  margin: 0 0 16px;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-family: ${fontFamily.font};
  font-size: 14px;
  text-align: left;

  &:focus {
    outline: none;
    border-color: ${primaryColor};
    box-shadow: 0 0 0 3px rgba(17, 138, 178, 0.12);
  }
`;
